import React, { useEffect, useRef } from 'react';
import { Box, Alert } from '@mui/material';

/**
 * ARIA Live Region Component
 *
 * WCAG-compliant live region for dynamically announcing validation errors
 * and status updates to screen readers without visual disruption.
 *
 * Implements WCAG 2.1 Level AA Success Criteria:
 * - 4.1.3 Status Messages (Level AA)
 * - 3.3.1 Error Identification (Level A)
 * - 3.3.3 Error Suggestion (Level AA)
 *
 * @param {Object} props
 * @param {string} props.message - Message to announce (error, warning, info, success)
 * @param {string} props.type - Message type: 'error' | 'warning' | 'info' | 'success'
 * @param {boolean} props.visible - Whether to show visual message (default: true)
 * @param {string} props.ariaLive - ARIA live politeness: 'polite' | 'assertive' | 'off' (default: auto-detected from type)
 * @param {boolean} props.ariaAtomic - Whether to read entire region on change (default: true)
 * @param {string} props.id - Optional ID for aria-describedby references
 */
export default function AriaLiveRegion({
  message,
  type = 'info',
  visible = true,
  ariaLive = null,
  ariaAtomic = true,
  id = null,
  sx = {},
  ...props
}) {
  const regionRef = useRef(null);

  // Auto-detect aria-live politeness based on message type
  const liveValue = ariaLive || (type === 'error' ? 'assertive' : 'polite');

  // Auto-detect role based on message type
  const roleValue = type === 'error' ? 'alert' : 'status';

  // Force screen reader announcement when message changes
  useEffect(() => {
    if (message && regionRef.current) {
      // Screen readers will announce when content changes in live region
      // No action needed - ARIA attributes handle this automatically
    }
  }, [message]);

  // Don't render if no message
  if (!message) {
    return null;
  }

  return (
    <Box
      ref={regionRef}
      id={id}
      role={roleValue}
      aria-live={liveValue}
      aria-atomic={ariaAtomic ? 'true' : 'false'}
      aria-relevant="additions text"
      sx={{
        marginTop: visible ? 2 : 0,
        marginBottom: visible ? 2 : 0,
        // Screen reader only mode - visually hidden but announced
        ...(visible
          ? {}
          : {
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }),
        ...sx,
      }}
      {...props}
    >
      {visible ? (
        <Alert
          severity={type}
          sx={{
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            '& .MuiAlert-icon': {
              fontSize: '20px',
            },
          }}
        >
          {message}
        </Alert>
      ) : (
        // Screen reader only content
        <span>{message}</span>
      )}
    </Box>
  );
}

/**
 * Hook for managing validation errors with ARIA announcements
 *
 * Usage:
 * const { error, setError, clearError, ErrorDisplay } = useAriaValidation();
 *
 * // Set error
 * setError('שדה זה הוא חובה');
 *
 * // Render error
 * <ErrorDisplay />
 */
export function useAriaValidation(initialError = '') {
  const [error, setErrorState] = React.useState(initialError);

  const setError = (message) => {
    setErrorState(message);
  };

  const clearError = () => {
    setErrorState('');
  };

  const ErrorDisplay = ({ visible = true, id = null, ...props }) => (
    <AriaLiveRegion
      message={error}
      type="error"
      visible={visible}
      id={id}
      {...props}
    />
  );

  return {
    error,
    setError,
    clearError,
    ErrorDisplay,
  };
}

/**
 * Hook for managing form-wide validation with multiple field errors
 *
 * Usage:
 * const { errors, setFieldError, clearFieldError, clearAllErrors, getFieldError, FormErrors } = useFormValidation();
 *
 * // Set field error
 * setFieldError('email', 'כתובת אימייל לא תקינה');
 *
 * // Get field error for aria-describedby
 * const emailError = getFieldError('email');
 *
 * // Clear specific field
 * clearFieldError('email');
 *
 * // Render all errors
 * <FormErrors />
 */
export function useFormValidation() {
  const [errors, setErrors] = React.useState({});

  const setFieldError = (fieldName, message) => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: message,
    }));
  };

  const clearFieldError = (fieldName) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  const clearAllErrors = () => {
    setErrors({});
  };

  const getFieldError = (fieldName) => {
    return errors[fieldName] || '';
  };

  const hasErrors = () => {
    return Object.keys(errors).length > 0;
  };

  const getErrorCount = () => {
    return Object.keys(errors).length;
  };

  const FormErrors = ({ visible = true, ...props }) => {
    const errorMessages = Object.values(errors).filter(Boolean);

    if (errorMessages.length === 0) {
      return null;
    }

    // Single error - show as single message
    if (errorMessages.length === 1) {
      return (
        <AriaLiveRegion
          message={errorMessages[0]}
          type="error"
          visible={visible}
          {...props}
        />
      );
    }

    // Multiple errors - show as list
    const combinedMessage = errorMessages.join('. ');

    return (
      <AriaLiveRegion
        message={combinedMessage}
        type="error"
        visible={visible}
        {...props}
      />
    );
  };

  const FieldError = ({ fieldName, visible = true, ...props }) => {
    const error = getFieldError(fieldName);

    if (!error) {
      return null;
    }

    return (
      <AriaLiveRegion
        message={error}
        type="error"
        visible={visible}
        id={`${fieldName}-error`}
        {...props}
      />
    );
  };

  return {
    errors,
    setFieldError,
    clearFieldError,
    clearAllErrors,
    getFieldError,
    hasErrors,
    getErrorCount,
    FormErrors,
    FieldError,
  };
}
